import { type ActionItem, MoreActions } from "@zfp-akademie-a-s/zfp-design";
import { CopyIcon, UsersIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { FeedbackIcon } from "Components/_Shared/Icons/Icons";
import type { AnalysisVersionInfo } from "Infrastructure/ApiZfp/Schema";
import { FeatureFlag, useFeatureFlag } from "Infrastructure/Hooks/useFeatureFlag";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useState } from "react";
import { CloseAndDuplicateDialog } from "./Duplicate/CloseAndDuplicateDialog";
import { DuplicateConflictDialog } from "./Duplicate/DuplicateConflictDialog";
import { useDuplicateAction } from "./Duplicate/useDuplicateAction";
import { ReportIssueModal } from "./ReportIssue/ReportIssueModal";
import { AnalysisSharingModal } from "./Sharing/AnalysisSharingModal";
import type { AnalysisAccess } from "./useAnalysisAccess";

type Props = {
  readonly analysisName: string;
  readonly displayedVersion: AnalysisVersionInfo | undefined;
  readonly latestVersion: AnalysisVersionInfo | undefined;
  readonly access: AnalysisAccess;
};

export const AnalysisMoreActions = ({ analysisName, displayedVersion, latestVersion, access }: Props) => {
  const { t } = useResource();
  const isAnalysisSharingEnabled = useFeatureFlag(FeatureFlag.AnalysisSharing);
  const isDuplicateEnabled = useFeatureFlag(FeatureFlag.Duplicate);
  const [isSharingModalOpen, setIsSharingModalOpen] = useState(false);
  const [isReportIssueModalOpen, setIsReportIssueModalOpen] = useState(false);
  const duplicateAction = useDuplicateAction({ isEnabled: isDuplicateEnabled, isOwner: access.isOwner, displayedVersion, latestVersion });

  const canManageAccess = isAnalysisSharingEnabled && access.isOwner && displayedVersion !== undefined;

  const items: ActionItem[] = [
    ...(canManageAccess
      ? [
          {
            label: t(Resources.AnalysisDetail.Sharing.Title),
            icon: <UsersIcon />,
            disabled: !access.isLatestVersion,
            disabledReason: t(Resources.AnalysisDetail.Sharing.LatestVersionOnly),
            onClick: () => setIsSharingModalOpen(true),
          },
        ]
      : []),
    ...(isDuplicateEnabled && displayedVersion !== undefined
      ? [
          {
            label: t(Resources.AnalysisDetail.Duplicate.Action),
            icon: <CopyIcon />,
            disabled: duplicateAction.isDisabled,
            disabledReason: duplicateAction.disabledReason ?? undefined,
            onClick: duplicateAction.start,
          },
        ]
      : []),
    {
      label: t(Resources.AnalysisDetail.ReportIssue.Title),
      icon: <FeedbackIcon />,
      onClick: () => setIsReportIssueModalOpen(true),
    },
  ];

  return (
    <>
      <MoreActions label={t(Resources.AnalysisDetail.MoreActions.Title)} items={items} />

      {canManageAccess && (
        <AnalysisSharingModal
          open={isSharingModalOpen}
          onClose={() => setIsSharingModalOpen(false)}
          analysisName={analysisName}
          version={displayedVersion.version}
          sharedWithAgent={access.sharedWithAgent}
        />
      )}
      <ReportIssueModal open={isReportIssueModalOpen} onClose={() => setIsReportIssueModalOpen(false)} />
      <DuplicateConflictDialog conflict={duplicateAction.conflict} />
      {latestVersion && (
        <CloseAndDuplicateDialog
          sourceVersion={duplicateAction.closingSourceVersion}
          latestVersion={latestVersion}
          onClose={duplicateAction.closeClosingDialog}
        />
      )}
    </>
  );
};
